/**
 * §11.5 Data Protection — the data lifecycle visual.
 *
 * A horizontal pipeline (Ingest → Store → Process → Retain → Delete)
 * with tone-tinted step cards.  On smaller screens the pipeline
 * collapses into a vertical stack.
 */

import React from 'react'
import clsx from 'clsx'
import {
  ArrowDownToLine, Database, Cpu, History, Trash2, Sparkles, ArrowRight,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { DATA_LIFECYCLE } from '../data'
import { TONE } from '../_shared'

const ICONS = { ArrowDownToLine, Database, Cpu, History, Trash2, Sparkles }

export default function DataProtection() {
  return (
    <section id="data-protection" className="relative py-24 md:py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Data Protection</Eyebrow>
          <SectionHeading align="center"
            title={<>Every stage of the{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">data lifecycle.</span></>}
            subtitle="From the moment data enters the platform to the moment it leaves, controls are applied consistently and are visible to the customer."
          />
        </div>

        {/* Pipeline */}
        <div className="relative">
          {/* Connector rail (hidden on mobile) */}
          <div className="pointer-events-none absolute inset-y-1/2 left-4 right-4 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent hidden lg:block" />

          <div className="grid gap-3 md:gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-5">
            {DATA_LIFECYCLE.map((s, i) => {
              const tone = TONE[s.tone] || TONE.orange
              const Icon = ICONS[s.iconKey] || Sparkles
              return (
                <div key={s.id} className="relative">
                  <div className="relative rounded-2xl border border-white/10 bg-white/[0.02] p-5 backdrop-blur-xl overflow-hidden h-full">
                    <div className="pointer-events-none absolute inset-0 opacity-40"
                      style={{ background: `radial-gradient(80% 40% at 20% 0%, rgba(${tone.rgb},0.10), transparent 70%)` }} />
                    <div className="relative flex flex-col gap-3">
                      <div className="flex items-center justify-between">
                        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl border"
                          style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                          <Icon strokeWidth={1.8} style={{ width: 18, height: 18 }} />
                        </span>
                        <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">step {i + 1}</span>
                      </div>
                      <div>
                        <h4 className="text-[14.5px] font-semibold text-white leading-tight">{s.label}</h4>
                        <p className="text-[13px] text-zinc-400 leading-relaxed mt-1.5">{s.body}</p>
                      </div>
                      <div className="mt-1 text-[11px] font-mono text-zinc-500 border-t border-white/5 pt-3">
                        {s.detail}
                      </div>
                    </div>
                  </div>

                  {/* Connector arrow between cards (desktop only) */}
                  {i < DATA_LIFECYCLE.length - 1 && (
                    <div className="hidden lg:flex absolute right-[-16px] top-1/2 -translate-y-1/2 z-10 h-6 w-6 items-center justify-center rounded-full border border-white/10 bg-black text-zinc-500">
                      <ArrowRight className="h-3 w-3" />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </Container>
    </section>
  )
}
